import type {
    ICreateCase,
    IUpdateCase,
    IListCasesQuery,
    ICaseListResponse,
    TJobStatus,
} from '@escloud/types';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { useApi, useTenant } from '@/context';

const POLL_INTERVAL_MS = 2000; // 2 seconds
const ACTIVE_JOB_STATUSES: TJobStatus[] = ['queued', 'processing'];

function hasActiveJobs(data: ICaseListResponse | undefined): boolean {
    if (!data) return false;

    return data.items.some(
        (caseItem) => caseItem.latestJob && ACTIVE_JOB_STATUSES.includes(caseItem.latestJob.status)
    );
}

/**
 * Every one of these queries is tenant-scoped: the API resolves the tenant from
 * the `X-Tenant-Id` header that ApiClient injects. The tenant is therefore part
 * of the REQUEST identity, and it has to be part of the CACHE identity too —
 * otherwise switching organization changes the header while the key stays the
 * same, and TanStack Query keeps serving the previous organization's cases with
 * nothing to trigger a refetch (the route pattern does not change on a switch,
 * so the page never remounts).
 */
export const caseKeys = {
    all: (tenantId: string) => ['cases', tenantId] as const,
    list: (tenantId: string, query?: IListCasesQuery) => [...caseKeys.all(tenantId), 'list', query] as const,
    detail: (tenantId: string, id: string) => [...caseKeys.all(tenantId), id] as const,
};

export function useCases(query?: IListCasesQuery) {
    const api = useApi();
    const { tenantId } = useTenant();

    return useQuery({
        queryKey: caseKeys.list(tenantId!, query),
        queryFn: () => api.cases.list(query),
        enabled: !!tenantId,
        refetchInterval: (queryResult) => {
            return hasActiveJobs(queryResult.state.data) ? POLL_INTERVAL_MS : false;
        },
    });
}

export function useCase(caseId: string | undefined) {
    const api = useApi();
    const { tenantId } = useTenant();

    return useQuery({
        queryKey: caseKeys.detail(tenantId!, caseId!),
        queryFn: () => api.cases.getById(caseId!),
        enabled: !!caseId && !!tenantId,
    });
}

export function useCreateCase() {
    const api = useApi();
    const queryClient = useQueryClient();
    const { tenantId } = useTenant();

    return useMutation({
        mutationFn: (data: ICreateCase) => api.cases.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: caseKeys.all(tenantId!) });
        },
    });
}

export function useUpdateCase(caseId: string) {
    const api = useApi();
    const queryClient = useQueryClient();
    const { tenantId } = useTenant();

    return useMutation({
        mutationFn: (data: IUpdateCase) => api.cases.update(caseId, data),
        onSuccess: (updatedCase) => {
            queryClient.setQueryData(caseKeys.detail(tenantId!, caseId), updatedCase);
            queryClient.invalidateQueries({ queryKey: caseKeys.all(tenantId!) });
        },
    });
}

export function useDeleteCase() {
    const api = useApi();
    const queryClient = useQueryClient();
    const { tenantId } = useTenant();

    return useMutation({
        mutationFn: (caseId: string) => api.cases.delete(caseId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: caseKeys.all(tenantId!) });
        },
    });
}
